import {
    Row,
    Col,
    Typography,
    Table,
    Button,
    message,
  } from "antd";
  import { routes } from "common/routes";
  import { generateHeaders } from "common/utils";
  import { AppPageHeader } from "components/AppPageHeader";
  import { useAppDispatch } from "hooks/useAppDispatch";
  import { useAppSelector } from "hooks/useAppSelector";
  import { useDocumentTitle } from "hooks/usePageTitle";
  import { useEffect, useMemo } from "react";
  import { useNavigate } from "react-router-dom";
import { cancelHoldAdjustment, removeProcessingHold, saveHoldAdjustment } from "store/stock-exchange/stockExchangeSlice";
  
  const MESSAGE_KEY = "SAVE_HOLD_ADJUSTMENT";
  
  export const SaveHoldExchangePage = () => {
    useDocumentTitle("Save Hold Adjustment");
    const { processingHoldAdjustment, status, error } = useAppSelector(
      (state) => state.stockExchangeServices
    );
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
  
    useEffect(() => {
      const { timeUploaded } = processingHoldAdjustment;
      if (timeUploaded) {
        const uploadedTime = new Date(timeUploaded).getTime();
        const currentTime = new Date().getTime();
        const minsPassed = (currentTime - uploadedTime) / 1000 / 60;
  
        if (minsPassed >= 15) {
          dispatch(removeProcessingHold());
        }
      }
  
      if (!Object.keys(processingHoldAdjustment).length) { 
        navigate(`/${routes.STOCK_EXCHANGE.UPLOAD_HOLD}`);
      }
    }, [dispatch, processingHoldAdjustment, navigate]);
  
    useEffect(() => {
      if (status === "loading") {
        message.loading({ content: "saving store exchange", key: MESSAGE_KEY });
      } else if (status === "success") {
        message.success({
          content: "Hold Adjustment saved!",
          duration: 5,
          key: MESSAGE_KEY,
        });
      } else if (status === "cancelling") {
        message.loading({ content: "cancelling store exchange", key: MESSAGE_KEY });
      } else if (status === "error") {
        message.error({ content: error, key: MESSAGE_KEY });
      }
  
      return () => {
        message.destroy();
      };
    }, [status, error]);
  
    let content;
    const { headerMapping } = processingHoldAdjustment;
    const { Text } = Typography;
  
    const generateMemoizedHeaders = useMemo(
      () => generateHeaders(headerMapping),
      [headerMapping]
    );
  
    const onSavePpd = () => {
      dispatch(saveHoldAdjustment());
    };
  
    const onCancelSave = () => {
      dispatch(cancelHoldAdjustment());
    };
  
    if ((processingHoldAdjustment.success) === 0) {
      content = <Text>Nothing to show here!</Text>;
    } else {
      const headers = generateMemoizedHeaders || {};
      const successRows = processingHoldAdjustment.successRows;
      const data = successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
      const columns = [
        ...Object.keys(headers).map(
          (key) =>
            ({
              key,
              title: headers[key],
              dataIndex: key,
            } as any)
        ),
      ];
      content = (
        <Table
          size="small"
          columns={columns}
          dataSource={data}
          scroll={{ y: 500, x: "100vh" }}
          summary={() => (
            <Table.Summary fixed="bottom">
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={24}>
                  <Row>
                    <Col>
                      <Button
                        type="primary"
                        onClick={onSavePpd}
                        loading={status === "loading"}
                        disabled={status === "loading"}
                      >
                        Save Hold Adjustment
                      </Button>
                    </Col>
                    <Col style={{ justifyContent: "left", marginLeft: "20px" }}>
                      <Button
                        onClick={onCancelSave}
                        disabled={status === "loading"}
                      >
                        Cancel
                      </Button>
                    </Col>
                  </Row>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        ></Table>
      );
    }
  
    return (
      <>
        <Row>
          <Col>
            <AppPageHeader title="Save Hold Adjustment"/>
          </Col>
        </Row>
        <Row>
          <Col xs={24}>{content}</Col>
        </Row>
      </>
    );
  };
  